import { useCallback, useEffect } from 'react';

import { useInView } from 'react-intersection-observer';

interface UseInfiniteScrollTriggerOptions {
  fetchNextPage: (options?: { cancelRefetch?: boolean }) => void;
  hasNextPage: boolean;
  isFetching: boolean;
  isError: boolean;
  root?: Element | null;
  rootMargin?: string;
}

interface UseInfiniteScrollTriggerResult {
  sentinelRef: (node?: Element | null) => void;
  canLoadMore: boolean;
  loadMore: () => void;
}

export function useInfiniteScrollTrigger({
  fetchNextPage,
  hasNextPage,
  isFetching,
  isError,
  root,
  rootMargin = '200px',
}: UseInfiniteScrollTriggerOptions): UseInfiniteScrollTriggerResult {
  const { ref: sentinelRef, inView } = useInView({ root, rootMargin });

  // Without `!isError`, a failed fetch reads the same as ready-for-more: the
  // sentinel is still in view because nothing was appended, so the trigger
  // re-fires and retries forever.
  const canLoadMore = hasNextPage && !isFetching && !isError;

  // `cancelRefetch: false` — the default `true` would abort an in-flight post-mutation
  // invalidation refetch and re-append onto its pre-mutation pages.
  const loadMore = useCallback(() => {
    if (!canLoadMore) return;
    fetchNextPage({ cancelRefetch: false });
  }, [canLoadMore, fetchNextPage]);

  useEffect(() => {
    if (inView) loadMore();
  }, [inView, loadMore]);

  return { sentinelRef, canLoadMore, loadMore };
}
